import { useCallback, useMemo } from 'react';

import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DropdownMenuBasic } from 'src/common/DropdownMenu/DropdownMenuBasic';
import { DropdownMenuItem } from 'src/common/DropdownMenu/DropdownMenuItem';
import { DriverCallRequestRead } from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { useTripRepository } from 'src/features/add-trip/useAddATripOnSubmit/useTripRepository';
import { useUserRoles } from 'src/features/auth/useUserRoles';
import {
  DispatchModalKey,
  setModalAssignmentId,
  showDispatchModal,
} from 'src/features/dispatch/dispatcher.slice';
import { useEditRouteTemplateModal } from 'src/features/trip/routeTemplates/hooks/useEditRouteTemplateModal';

import { AssignmentColumnExpandButton } from './AssignmentColumnExpandButton';
import { EditAssignmentMenuItem } from './EditAssignmentMenuItem';
import {
  useAssignmentInfo,
  useCallRequestHandler,
  useReactivateAssignment,
} from './hooks';

interface AssignmentHeaderMenuProps {
  assignmentId: string;
  pendingCallRequest?: DriverCallRequestRead;
}

export function AssignmentHeaderMenu({
  assignmentId,
  pendingCallRequest,
}: AssignmentHeaderMenuProps) {
  const dispatch = useAppDispatch();
  const { openCallRequestModal } = useCallRequestHandler();
  const { driverHasTrips } = useAssignmentInfo(assignmentId);
  const { reactivateAssignment } = useReactivateAssignment(assignmentId);
  const { isRestrictedSupport } = useUserRoles();
  const onClickMenuItem = useCallback(
    (key: DispatchModalKey) => {
      if (key === 'callRequest' && pendingCallRequest) {
        openCallRequestModal(pendingCallRequest, assignmentId);
      } else {
        dispatch(setModalAssignmentId(assignmentId));
        dispatch(showDispatchModal(key));
      }
    },
    [openCallRequestModal, dispatch, assignmentId, pendingCallRequest]
  );

  const dispatchResponse = useAppSelector(
    (state) => state.dispatcher.dispatchResponse
  );

  const assignedTrips = useMemo(
    () => dispatchResponse?.assigned_trips ?? [],
    [dispatchResponse]
  );

  const tripsInAssignment = useMemo(
    () => assignedTrips.filter((trip) => trip.assignment_id === assignmentId),
    [assignedTrips, assignmentId]
  );

  const assignments = useMemo(
    () => dispatchResponse?.assignments ?? [],
    [dispatchResponse]
  );
  const { editBulkRouteTemplateModal } = useEditRouteTemplateModal();
  const openRouteTemplateGenerator = useCallback(() => {
    const filteredAssignments = assignments.filter(
      (assignment) => assignment.id === assignmentId
    );
    editBulkRouteTemplateModal(filteredAssignments, assignedTrips);
  }, [assignedTrips, assignmentId, assignments, editBulkRouteTemplateModal]);

  const tripRepository = useTripRepository();

  const handleUnlockAllTrips = useCallback(async () => {
    dispatch(
      show({
        type: 'info',
        title: 'Unlocking all trips for the assignment',
        description: 'This may take a few moments',
      })
    );
    const failedToUnlock = [];
    for (const trip of tripsInAssignment) {
      try {
        await tripRepository.unlock(trip.id);
      } catch {
        failedToUnlock.push(trip.id);
      }
    }
    if (failedToUnlock.length > 0) {
      dispatch(
        show({
          title: 'Error unlocking some trips',
          description: `Failed to unlock: ${failedToUnlock.length} trips`,
          type: 'error',
        })
      );
      return;
    }

    dispatch(
      show({
        title: 'All trips for the assignment have been unlocked',
        type: 'success',
      })
    );
  }, [tripsInAssignment, dispatch, tripRepository]);

  const handleLockAllTrips = useCallback(async () => {
    dispatch(
      show({
        title: 'Locking all trips for the assignment',
        description: 'This may take a few moments',
      })
    );
    const failedToLock = [];
    for (const trip of tripsInAssignment) {
      try {
        await tripRepository.lock(trip.id);
      } catch {
        failedToLock.push(trip.id);
      }
    }

    if (failedToLock.length > 0) {
      dispatch(
        show({
          title: 'Error locking some trips',
          description: `Failed to lock: ${failedToLock.length} trips`,
          type: 'error',
        })
      );
      return;
    }

    dispatch(
      show({
        title: 'All trips for the assignment have been locked',
        type: 'success',
      })
    );
  }, [tripsInAssignment, dispatch, tripRepository]);

  return (
    <>
      <DropdownMenuBasic
        trigger={
          <EllipsisVerticalIcon
            className="h-4 w-4"
            data-testid="event-menu-trigger"
          />
        }
      >
        <MenuItem
          label="Handle call request"
          condition={!!pendingCallRequest}
          onClick={() => onClickMenuItem('callRequest')}
        />
        {!isRestrictedSupport && (
          <EditAssignmentMenuItem assignmentId={assignmentId} />
        )}
        <MenuItem
          label="Add a break"
          onClick={() => onClickMenuItem('assignmentBreak')}
        />
        <MenuItem
          label="Unassign all trips"
          condition={driverHasTrips}
          onClick={() => onClickMenuItem('unassign')}
        />
        <MenuItem
          label="Clock driver back in"
          condition={!!reactivateAssignment}
          onClick={() => reactivateAssignment?.()}
        />
        <AssignmentColumnExpandButton assignmentId={assignmentId} />
        <MenuItem
          label="Create Route Template"
          condition={driverHasTrips}
          onClick={openRouteTemplateGenerator}
        />
        <MenuItem
          label="Unlock all trips"
          condition={driverHasTrips}
          onClick={handleUnlockAllTrips}
        />
        <MenuItem
          label="Lock all trips"
          condition={driverHasTrips}
          onClick={handleLockAllTrips}
        />
      </DropdownMenuBasic>
    </>
  );
}

interface MenuItemInputs {
  label: string;
  condition?: boolean;
  onClick: () => void;
}

export const MenuItem = ({ label, condition, onClick }: MenuItemInputs) => {
  const shouldRender = condition ?? true;
  const testId =
    label.toLowerCase().replace(new RegExp(' ', 'g'), '-') + '-item';
  return shouldRender ? (
    <DropdownMenuItem data-testid={testId} onClick={onClick}>
      {label}
    </DropdownMenuItem>
  ) : null;
};
